// src/pages/Carrinho.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './Carrinho.css';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const Carrinho = () => {
    const { cartItems, removeFromCart, updateQuantity, clearCart, cartCount } = useCart();
    const { currentUser, userProfile, updateUserProfile } = useAuth();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [customerData, setCustomerData] = useState({
        nome: '',
        sobrenome: '',
        telefone: '',
        cpfCnpj: '', 
        rua: '', 
        numero: '', 
        complemento: '', 
        bairro: '', 
        cidade: '', 
        estado: '', 
        cep: '', 
        projectDetails: ''
    });

    useEffect(() => {
        if (userProfile) {
            const nameParts = userProfile.nome ? userProfile.nome.split(' ') : [''];
            const nome = nameParts.shift();
            const sobrenome = nameParts.join(' ');

            setCustomerData(prevData => ({
                ...prevData,
                nome: nome || '',
                sobrenome: sobrenome || '',
                telefone: userProfile.telefone || '',
                cpfCnpj: userProfile.cpfCnpj || '',
                rua: userProfile.rua || '',
                numero: userProfile.numero || '',
                complemento: userProfile.complemento || '',
                bairro: userProfile.bairro || '',
                cidade: userProfile.cidade || '',
                estado: userProfile.estado || '',
                cep: userProfile.cep || '',
            }));
        }
    }, [userProfile]);

    const handleDataChange = (e) => {
        const { name, value } = e.target;
        setCustomerData(prev => ({ ...prev, [name]: value }));
    };

    const handleLogin = () => {
        alert('Você precisa fazer login para continuar. Você será redirecionado.');
        navigate('/'); 
    };

    const handleSubmitQuote = async () => {
        if (!currentUser) return alert('Erro: Usuário não está logado.');
        if (!customerData.nome || !customerData.telefone || !customerData.cpfCnpj) {
            return alert('Por favor, preencha seu Nome, Telefone e CPF/CNPJ para continuar.');
        }
        setIsSubmitting(true);
        try {
            const profileDataToUpdate = { ...customerData, nome: `${customerData.nome} ${customerData.sobrenome}`.trim() };
            delete profileDataToUpdate.projectDetails;
            
            await updateUserProfile(profileDataToUpdate);

            const quoteData = {
                userId: currentUser.uid, 
                userName: `${customerData.nome} ${customerData.sobrenome}`.trim(), 
                userEmail: currentUser.email,
                items: cartItems, 
                customerInfo: customerData,
                status: 'nova', 
                createdAt: serverTimestamp()
            };
            
            await addDoc(collection(db, 'users', currentUser.uid, 'cotacoes'), quoteData);
            
            alert('Sua solicitação de cotação foi enviada com sucesso!');
            clearCart();
            navigate('/dashboard');
        } catch (error) {
            console.error("Erro ao enviar cotação:", error);
            alert('Ocorreu um erro ao enviar sua cotação.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const subtotal = cartItems.reduce((total, item) => total + (item.precoSugerido * item.quantity), 0);
    const formatPrice = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

    return (
        <div className="cart-page-container">
            <div className="container">
                <h1>Minha Cotação</h1>
                {cartCount === 0 ? (
                    <div className="cart-empty"><p>Seu carrinho está vazio.</p><Link to="/produtos/intelbras" className="cta-link">Ver produtos</Link></div>
                ) : (
                    <div className="cart-layout">
                        <div className="cart-items-list">
                            <table>
                                <thead><tr><th colSpan="2">Produto</th><th>Preço Unit.</th><th>Quantidade</th><th>Subtotal</th><th></th></tr></thead>
                                <tbody>
                                    {cartItems.map(item => (
                                        <tr key={item.id}>
                                            <td className="item-image"><img src={item.mainImage} alt={item.nome} /></td>
                                            <td className="item-details"><p className="item-name">{item.nome}</p><p className="item-sku">SKU: {item.sku}</p></td>
                                            <td>{formatPrice(item.precoSugerido)}</td>
                                            <td><input type="number" min="1" value={item.quantity} onChange={(e) => updateQuantity(item.id, e.target.value)} className="quantity-input"/></td>
                                            <td>{formatPrice(item.precoSugerido * item.quantity)}</td>
                                            <td><button onClick={() => removeFromCart(item.id)} className="remove-button">×</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="cart-summary">
                            <h2>Resumo da Cotação</h2>
                            <div className="summary-row"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                            <p className="summary-note">O valor final será enviado em sua proposta comercial.</p><hr />
                            {currentUser ? (
                                <div className="quote-submission">
                                    <h3>Dados para Cotação</h3>
                                    <div className="form-group-grid">
                                        <div className="form-group-cart"><label>Nome *</label><input type="text" name="nome" value={customerData.nome} onChange={handleDataChange} required /></div>
                                        <div className="form-group-cart"><label>Sobrenome</label><input type="text" name="sobrenome" value={customerData.sobrenome} onChange={handleDataChange} /></div>
                                        <div className="form-group-cart full-span"><label>Telefone *</label><input type="text" name="telefone" value={customerData.telefone} onChange={handleDataChange} required /></div>
                                        <div className="form-group-cart full-span"><label>CPF ou CNPJ *</label><input type="text" name="cpfCnpj" value={customerData.cpfCnpj} onChange={handleDataChange} required /></div>
                                        <div className="form-group-cart full-span"><label>Endereço de Instalação (Rua, Av.)</label><input type="text" name="rua" value={customerData.rua} onChange={handleDataChange} /></div>
                                        <div className="form-group-cart"><label>Número</label><input type="text" name="numero" value={customerData.numero} onChange={handleDataChange} /></div>
                                        <div className="form-group-cart"><label>Complemento</label><input type="text" name="complemento" value={customerData.complemento} onChange={handleDataChange} /></div>
                                        <div className="form-group-cart full-span"><label>Bairro</label><input type="text" name="bairro" value={customerData.bairro} onChange={handleDataChange} /></div>
                                        <div className="form-group-cart"><label>Cidade</label><input type="text" name="cidade" value={customerData.cidade} onChange={handleDataChange} /></div>
                                        <div className="form-group-cart"><label>Estado</label><input type="text" name="estado" value={customerData.estado} onChange={handleDataChange} /></div>
                                        <div className="form-group-cart full-span"><label>CEP</label><input type="text" name="cep" value={customerData.cep} onChange={handleDataChange} /></div>
                                    </div>
                                    <h3>Detalhes do Projeto</h3>
                                    <textarea name="projectDetails" rows="4" placeholder="Informe aqui detalhes importantes para a instalação..." value={customerData.projectDetails} onChange={handleDataChange}></textarea>
                                    <button onClick={handleSubmitQuote} className="submit-quote-button" disabled={isSubmitting}>{isSubmitting ? 'Enviando...' : 'Enviar Solicitação de Cotação'}</button>
                                </div>
                            ) : (
                                <div className="login-prompt"><p>Você precisa fazer o login para solicitar a cotação.</p><button onClick={handleLogin} className="login-button">Entrar ou Cadastrar-se</button></div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Carrinho;